import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CustomScheduling from './CustomScheduling';
import DefaultScheduling from './DefaultScheduling';
import LiveCalendar from './LiveCalendar';
import { buildRulesEnginePayload } from './customScheduleRuleBuilder';
import {
  generateMockLiveInfoForMonth,
  readAvailabilityStore,
  writeAvailabilityStore,
} from './storage';
import CalendarDaySlotsDialog from './CalendarDaySlotsDialog';

function initLiveInfo(propValue) {
  if (propValue && typeof propValue === 'object') return { ...propValue };
  const existing = readAvailabilityStore().liveInfo;
  if (
    existing &&
    typeof existing === 'object' &&
    Object.keys(existing).length > 0
  ) {
    return { ...existing };
  }
  const mock = generateMockLiveInfoForMonth(dayjs());
  writeAvailabilityStore({ liveInfo: mock });
  return mock;
}

/**
 * Service provider availability: monthly calendar, default weekly grid, and per-day custom hours.
 * @param {{
 *   liveCalendarValue?: Record<string, { working?: boolean, slots?: string[] }>,
 *   defaultScheduleValue?: unknown[],
 * }} props
 */
function AvailabilityScheduler({ liveCalendarValue, defaultScheduleValue }) {
  const [panel, setPanel] = useState('calendar');
  const [customDate, setCustomDate] = useState(null);
  const [liveInfo, setLiveInfo] = useState(() =>
    initLiveInfo(liveCalendarValue),
  );
  const [customSchedulesList, setCustomSchedulesList] = useState(() => {
    const s = readAvailabilityStore();
    return Array.isArray(s.customSchedulesList) ? s.customSchedulesList : [];
  });

  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dialogSelectedDate, setDialogSelectedDate] = useState(null);

  useEffect(() => {
    if (liveCalendarValue && typeof liveCalendarValue === 'object') {
      setLiveInfo({ ...liveCalendarValue });
    }
  }, [liveCalendarValue]);

  const applyCustomScheduleSave = useCallback(next => {
    if (next && typeof next === 'object' && !Array.isArray(next)) {
      const patch = {};
      if ('liveInfo' in next && next.liveInfo != null) {
        setLiveInfo(next.liveInfo);
        patch.liveInfo = next.liveInfo;
      }
      if (next.customSchedulesList != null) {
        setCustomSchedulesList(next.customSchedulesList);
        patch.customSchedulesList = next.customSchedulesList;
        patch.customScheduleRulesEngine = buildRulesEnginePayload(
          next.customSchedulesList,
        );
      }
      if (next.customScheduleRules != null) {
        patch.customScheduleRules = next.customScheduleRules;
      }
      if (next.customScheduleForm != null) {
        patch.customScheduleForm = next.customScheduleForm;
      }
      if (Object.keys(patch).length > 0) {
        writeAvailabilityStore(patch);
      }
    } else {
      setLiveInfo(next);
      writeAvailabilityStore({ liveInfo: next });
    }
  }, []);

  const closeDayDialog = useCallback(() => {
    setDayDialogOpen(false);
    setDialogSelectedDate(null);
  }, []);

  const handleDayClick = useCallback(d => {
    if (!d || !dayjs.isDayjs(d)) return;
    setDialogSelectedDate(d.startOf('day'));
    setDayDialogOpen(true);
  }, []);

  const handleCustomScheduleSave = useCallback(
    (next, variant) => {
      applyCustomScheduleSave(next);
      if (variant === 'page' && next?.customSchedulesList == null) {
        setPanel('calendar');
        setCustomDate(null);
      }
    },
    [applyCustomScheduleSave],
  );

  const handleDaySlotsSave = useCallback(
    nextLive => {
      applyCustomScheduleSave({ liveInfo: nextLive });
    },
    [applyCustomScheduleSave],
  );

  const handleCustomCancel = useCallback(() => {
    setPanel('calendar');
    setCustomDate(null);
  }, []);

  const openCustomMenu = useCallback(() => {
    setCustomDate(null);
    setPanel('custom');
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {panel === 'calendar' && (
          <LiveCalendar
            value={liveInfo}
            onDayClick={handleDayClick}
            toolbarActions={
              <>
                <Button variant="contained" onClick={() => setPanel('default')}>
                  Default Schedule
                </Button>
                <Button variant="contained" onClick={openCustomMenu}>
                  Special Schedules
                </Button>
              </>
            }
          />
        )}

        {panel === 'default' && (
          <Box>
            <DefaultScheduling
              setPanel={setPanel}
              value={defaultScheduleValue}
            />
          </Box>
        )}

        {panel === 'custom' && (
          <Box>
            <CustomScheduling
              selectedDate={customDate}
              liveInfo={liveInfo}
              customSchedulesList={customSchedulesList}
              onSave={handleCustomScheduleSave}
              onCancel={handleCustomCancel}
              setPanel={setPanel}
              variant="page"
            />
          </Box>
        )}

        <CalendarDaySlotsDialog
          open={dayDialogOpen}
          onClose={closeDayDialog}
          date={dialogSelectedDate}
          liveInfo={liveInfo}
          onSave={handleDaySlotsSave}
        />
      </Box>
    </LocalizationProvider>
  );
}

export default AvailabilityScheduler;
